/**
 * Custom Selenological WebGL Shaders
 * Implements LOLA hypsometric elevation false-color tinting,
 * Lommel-Seeliger regolith scattering, and slope curvature analysis.
 */

export const LOLATopographyShader = {
  uniforms: {
    tDisplacement: { value: null },
    tAlbedo: { value: null },
    uSunDirection: { value: null },
    uSunColor: { value: null },
    uAmbientColor: { value: null },
    uMode: { value: 0 } // 0: Albedo, 1: LOLA False Color, 2: Shaded Relief / Slope
  },
  vertexShader: `
    varying vec2 vUv;
    varying vec3 vNormal;
    varying vec3 vWorldPosition;

    void main() {
      vUv = uv;
      vNormal = normalize(normalMatrix * normal);
      vec4 worldPos = modelMatrix * vec4(position, 1.0);
      vWorldPosition = worldPos.xyz;
      gl_Position = projectionMatrix * viewMatrix * worldPos;
    }
  `,
  fragmentShader: `
    uniform sampler2D tDisplacement;
    uniform sampler2D tAlbedo;
    uniform vec3 uSunDirection;
    uniform vec3 uSunColor;
    uniform vec3 uAmbientColor;
    uniform int uMode; // 0: albedo, 1: topography, 2: slope relief

    varying vec2 vUv;
    varying vec3 vNormal;
    varying vec3 vWorldPosition;

    // Scientific LOLA elevation colormap palette
    vec3 lolaColorMap(float h) {
      // h in [0.0, 1.0] representing -9.1 km to +10.7 km
      if (h < 0.15) {
        // Deep trench / South Pole-Aitken Basin (Deep Indigo to Navy)
        return mix(vec3(0.12, 0.0, 0.35), vec3(0.0, 0.15, 0.55), h / 0.15);
      } else if (h < 0.35) {
        // Low Mare Basins (Navy to Cyan)
        return mix(vec3(0.0, 0.15, 0.55), vec3(0.0, 0.75, 0.85), (h - 0.15) / 0.20);
      } else if (h < 0.55) {
        // Highland Transitions (Cyan to Emerald Green)
        return mix(vec3(0.0, 0.75, 0.85), vec3(0.15, 0.75, 0.2), (h - 0.35) / 0.20);
      } else if (h < 0.75) {
        // Terrae / Highlands (Emerald to Vivid Amber/Yellow)
        return mix(vec3(0.15, 0.75, 0.2), vec3(0.95, 0.85, 0.1), (h - 0.55) / 0.20);
      } else if (h < 0.90) {
        // High Mountain Rings (Yellow to Crimson Red)
        return mix(vec3(0.95, 0.85, 0.1), vec3(0.90, 0.15, 0.1), (h - 0.75) / 0.15);
      } else {
        // Dirichlet-Jackson / Highest Peaks (Crimson to Snow White)
        return mix(vec3(0.90, 0.15, 0.1), vec3(1.0, 1.0, 1.0), (h - 0.90) / 0.10);
      }
    }

    void main() {
      vec3 normal = normalize(vNormal);
      vec3 lightDir = normalize(uSunDirection);

      // Lommel-Seeliger / Lambertian diffuse hybrid
      float nDotL = dot(normal, lightDir);
      float diffuse = max(0.0, nDotL);

      // Regolith opposition surge / retro-reflection backscatter
      vec3 viewDir = normalize(cameraPosition - vWorldPosition);
      float cosAlpha = max(0.0, dot(lightDir, viewDir));
      float surge = pow(cosAlpha, 12.0) * 0.25;

      vec4 albedoTex = texture2D(tAlbedo, vUv);
      float elev = texture2D(tDisplacement, vUv).r;

      vec3 baseColor;
      if (uMode == 0) {
        // True Photographic LROC Albedo
        baseColor = albedoTex.rgb;
      } else if (uMode == 1) {
        // LOLA Topographic False Color
        baseColor = lolaColorMap(elev);
      } else {
        // Shaded Relief / Slope Morphology
        baseColor = vec3(elev * 0.85 + 0.15);
      }

      // Lunar lighting computation: direct sun + earthshine ambient + regolith surge
      vec3 illumination = uAmbientColor + uSunColor * (diffuse + surge);
      vec3 finalColor = baseColor * illumination;

      gl_FragColor = vec4(finalColor, 1.0);
    }
  `
};
